import type { HoleInfo, HoleScore, Team } from '@/lib/golfEngine';
import { cn } from '@/lib/utils';

interface SummaryGridProps {
  holes: HoleInfo[];
  teams: Team[];
  scores: HoleScore[];
  onHoleTap: (holeIndex: number) => void;
  activeHoleIndex: number | null;
}

function getScoreColor(score: number, par: number): string {
  const diff = score - par;
  if (diff <= -2) return 'text-yellow-600 font-bold';
  if (diff === -1) return 'text-green-600 font-bold';
  if (diff === 0) return 'text-gray-700';
  if (diff === 1) return 'text-red-500 font-bold';
  return 'text-red-700 font-bold';
}

export function SummaryGrid({ holes, teams, scores, onHoleTap, activeHoleIndex }: SummaryGridProps) {
  const allPlayers = teams.flatMap((t) => t.players);
  const frontNine = holes.slice(0, 9);
  const backNine = holes.slice(9, 18);

  const getScore = (playerId: string, holeNumber: number) =>
    scores.find((s) => s.playerId === playerId && s.holeNumber === holeNumber);

  const renderHalf = (holeSet: HoleInfo[], label: string) => (
    <div className="mb-3">
      <div className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider mb-1 px-1">{label}</div>
      <div className="overflow-x-auto">
        <table className="w-full text-xs border-collapse border border-gray-200 rounded" data-testid={`grid-${label.toLowerCase()}`}>
          <thead>
            <tr className="bg-gray-100">
              <th className="sticky left-0 z-10 bg-gray-100 px-2 py-1.5 text-left border-b border-r border-gray-200 text-[10px] font-medium text-muted-foreground w-[52px]">Hole</th>
              {holeSet.map((h) => (
                <th
                  key={h.number}
                  className={cn(
                    'px-0 py-1.5 border-b border-r border-gray-200 text-center font-bold min-w-[34px] cursor-pointer transition-colors',
                    activeHoleIndex === h.number - 1 ? 'bg-primary/20' : 'hover:bg-gray-200'
                  )}
                  onClick={() => onHoleTap(h.number - 1)}
                  data-testid={`grid-hole-header-${h.number}`}
                >
                  {h.number}
                </th>
              ))}
            </tr>
            <tr className="bg-gray-50">
              <td className="sticky left-0 z-10 bg-gray-50 px-2 py-1 text-left border-b border-r border-gray-200 text-[10px] text-muted-foreground">Par</td>
              {holeSet.map((h) => (
                <td key={h.number} className="px-0 py-1 border-b border-r border-gray-200 text-center text-muted-foreground text-[10px]">
                  {h.par}
                </td>
              ))}
            </tr>
          </thead>
          <tbody>
            {allPlayers.map((player) => {
              const teamIdx = teams[0].players.some((p) => p.id === player.id) ? 0 : 1;
              return (
                <tr key={player.id} className={teamIdx === 0 ? 'bg-primary/5' : 'bg-[#9966CC]/5'}>
                  <td className="sticky left-0 z-10 px-2 py-1.5 border-b border-r border-gray-200 font-medium truncate max-w-[52px] text-[10px] bg-inherit">
                    {player.name}
                  </td>
                  {holeSet.map((h) => {
                    const sc = getScore(player.id, h.number);
                    const grossScore = sc?.grossScore;
                    return (
                      <td
                        key={h.number}
                        className={cn(
                          'px-0 py-1.5 border-b border-r border-gray-200 text-center cursor-pointer transition-colors min-w-[34px]',
                          activeHoleIndex === h.number - 1 ? 'bg-primary/20' : 'hover:bg-gray-100'
                        )}
                        onClick={() => onHoleTap(h.number - 1)}
                        data-testid={`grid-cell-${player.id}-${h.number}`}
                      >
                        {grossScore !== null && grossScore !== undefined ? (
                          <span className={cn('text-xs', getScoreColor(grossScore, h.par))}>{grossScore}</span>
                        ) : (
                          <span className="text-gray-300 text-[10px]">-</span>
                        )}
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );

  return (
    <div className="px-2 py-3" data-testid="summary-grid">
      {renderHalf(frontNine, 'Front 9')}
      {renderHalf(backNine, 'Back 9')}
    </div>
  );
}
